<template>
    <div class="modal-panel" :class="(logoRequestObj && logoRequestObj.logo) ? 'gap-sm' : 'gap-lg'">
        <div :class="{ initiated: (loading && requestingLogo || (!loading && logoRequestObj)) }">
            <div class="resultswrap">
                <transition name="pop" appear mode="out-in">
                    <div v-if="!loading && !logoRequestObj">
                        <AnimationItem 
                        class="anim-idle" 
                        :animation="{ animationData: idleAnimation.default }" 
                        :key="'anim_idle'"/>
                    </div>
                </transition>

                <transition name="pop" appear mode="out-in">
                  <div v-if="loading && logoRequestObj" class="loading-img-wrap">
                    <img :src="loadingGif" class="loading-img">
                    <div class="gk-ai-assistant">
                      {{ $gettext('Hold on for a second... We are creating the logo for you!') }}
                      <br>
                      <svg id="dots" width="32px" height="32px" viewBox="0 0 132 58" version="1.1" xmlns="http://www.w3.org/2000/svg">
                        <g stroke="none" stroke-width="1" fill="none" fill-rule="evenodd">
                          <g id="dots" fill="#3333337d">
                              <circle id="dot1" cx="25" cy="30" r="13"></circle>
                              <circle id="dot2" cx="65" cy="30" r="13"></circle>
                              <circle id="dot3" cx="105" cy="30" r="13"></circle>
                          </g>
                        </g>
                      </svg>
                    </div>
                  </div>
                </transition>

                <transition name="pop" appear mode="out-in">
                  <div v-if="!loading && logoRequestObj" class="logo-img-wrap">
                    <img class="logo-img"
                    v-if="logoRequestObj.logo"
                    :src="logoRequestObj.logo.image"
                    :alt="$gettext('AI Generated Logo')">
                  </div>
                </transition>

                <transition name="pop" appear mode="out-in">
                    <div v-if="!loading && logoRequestObj" class="modal-panel--selectedlogo">
                        <div>
                          <h3>{{ logoRequestObj.message }}</h3>
                          <span>{{ designStyleOptions.find(x => x.value === logoRequestObj.design_style).text }}, 
                              {{ colorPaletteOptions.find(x => x.value === logoRequestObj.color_palette).text }}</span>
                        </div>
                    </div>
                </transition>
            </div>
        </div>

        <div v-show="!loading" class="action-container gk-ai-assistant">
            <div class="prompt-wrap">
                <div class="prompt-wrap__options">
                    <div class="d-flex">
                        <b-form-group label-class="gk-label-margin gk-modal-label">
                            <b-input-group>
                                <b-form-select size="sm" v-model="selectedDesignStyle" :options="designStyleOptions"></b-form-select>
                            </b-input-group>
                        </b-form-group>
                    </div>

                    <div class="d-flex">
                        <b-form-group label-class="gk-label-margin gk-modal-label">
                            <b-input-group>
                                <b-form-select size="sm" v-model="selectedColorPalette" :options="colorPaletteOptions"></b-form-select>
                            </b-input-group>
                        </b-form-group>
                    </div>

                    <GekkoButton 
                        v-if="!loading && logoRequestObj"
                        @click="$emit('openEdit')"
                        :text="$gettext('edit')"
                        variant="link"
                        icon="fa fa-pencil"/>
                </div>

                <div class="prompt">
                  <b-form-textarea
                    v-model="requestMessage"
                    @keydown="sendPromptOnEnter"
                    :placeholder="$gettext('Design a logo for an accounting company, showing a green smiling and friendly lizard.')"
                    rows="4"
                    no-resize/>
                  <GekkoButton 
                    @click="sendLogoRequest"
                    :disabled="(loading || !requestMessage || requestMessage.length < 0)"
                    :text="$gettext('Send')"
                    :icon="'fa-solid fa-paper-plane-top'"
                    class="send-message-btn"/>
                  <div class="prompt__text-count" :class="{ maxfill: requestMessage.length >= maxPromptLength }">
                    {{ requestMessage.length }}/{{ maxPromptLength }}
                  </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from 'vuex'
import * as idleAnimation from '@/css/animations/idle.json'
import ModalBase from '@/components/base_components/ModalBase'
import TitleBar from '@/components/TitleBar'
import GekkoButton from '@/components/GekkoButton'
import AnimationItem from '@/components/dashboard/ui/AnimationItem.vue'
import Avatar from "@/components/Avatar"
import AiAssistantService from '@/store/services/ai.assistant.service.js'

export default {
  name: 'GenerateLogo',
  components: {
    ModalBase,
    TitleBar,
    GekkoButton,
    AnimationItem,
    Avatar
  },
  data () {
    return {
      logoBotId: null,
      designStyleOptions: [],
      colorPaletteOptions: [],
      selectedDesignStyle: null,
      selectedColorPalette: null,
      requestMessage: '',
      logoRequestObj: null,
      loading: false,
      requestingLogo: false,
      maxPromptLength: null,
      idleAnimation: idleAnimation
    }
  },
  computed: {
    ...mapGetters ({
      currentUser: 'main/getCurrentUser'
    }),
    loadingGif () {
      let ranInt = Math.floor(Math.random() * 6)
      let loading_gif = 'dali'
      switch (ranInt) {
        case 0:
          loading_gif = 'dali'
          break;
        case 1:
          loading_gif = 'magritte'
          break;
        case 2:
          loading_gif = 'monalisa'
          break;
        case 3:
          loading_gif = 'picasso'
          break;
        case 4:
          loading_gif = 'van_gogh'
          break;
        case 5:
          loading_gif = 'vermeer'
          break;
      }
      return '/static/public/img/loading-gifs/' + loading_gif + '.gif'
    }
  },
  created () {
    this.setupData()
  },
  mounted () {
    document.body.classList.add('modal-on')
  },
  methods: {
    close () {
      this.$emit('close', false)
      document.body.classList.remove('modal-on')
    },
    setupData () {
      this.loading = true
      AiAssistantService.getCurrentLogoBot().then((response) => {
        this.maxPromptLength = response.data.max_prompt_length
        this.logoBotId = response.data.id

        AiAssistantService.getLogoStyleOptions().then((response) => {
          this.designStyleOptions = response.data.design_styles.filter(x => x.type === 'style').map(option => {
            return {
              value: option.id,
              text: option.name
            }
          })

          this.colorPaletteOptions = response.data.color_palettes.filter(x => x.type === 'palette').map(option => {
            return {
              value: option.id,
              text: option.name
            }
          })
          this.selectedDesignStyle = this.designStyleOptions[0].value
          this.selectedColorPalette = this.colorPaletteOptions[0].value

          this.loading = false
        })
      })
    },
    sendLogoRequest () {
      this.loading = true
      this.requestingLogo = true
      this.logoRequestObj = {
        bot_id: this.logoBotId,
        message: this.requestMessage,
        design_style: this.selectedDesignStyle,
        color_palette: this.selectedColorPalette
      }

      const logoRequestCopy = Object.assign({}, this.logoRequestObj)

      logoRequestCopy.message = this.requestMessage
      logoRequestCopy.design_style = this.selectedDesignStyle
      logoRequestCopy.color_palette = this.selectedColorPalette
        
      AiAssistantService.sendLogoRequest(logoRequestCopy).then((response) => {
        this.requestMessage = ''
        this.logoRequestObj = response.data

        this.requestingLogo = false
        this.loading = false
      }).catch((error) => {
        console.log('error', error)
        let msg = this.$gettext('GekkoBot not responding.')
        if (error.status === 403) {
          msg = this.$gettext('Please consider upgrading to Premium to keep using GekkoBot.')
          this.$store.commit('togglePremiumModal', 'web_ai_assistant')
          if (window.mixpanel) {
            window.mixpanel.track("Gekkobot: error - limit of free consultations")
          }
        } else {
          if (error.status === 405) {
            msg = this.$gettext('You are not allowed to use the chat bot feature.')
            if (window.mixpanel) {
              window.mixpanel.track("Gekkobot: error - not allowed")
            }
          } else if (error.status === 429) {
            msg = this.$gettext('You have already sent too many messages.')
            if (window.mixpanel) {
              window.mixpanel.track("Gekkobot: error - too many messages")
            }
          }
          this.$store.commit('setAlert', {
            title: this.$gettext('Error'),
            text: msg,
            variant: "danger",
            duration: 4
          })
        }
        this.loading = false
      })
    },
    sendPromptOnEnter (e) {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (this.requestMessage && !!this.requestMessage.trim()) {
          this.sendLogoRequest()
        } else {
          this.requestMessage = null
        }
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.logo-img-wrap {
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;

  .logo-img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
}

.loading-img-wrap {
  border-radius: $radiusLg;
  box-shadow: 2px 2px 10px rgba(0, 0, 0, 0.5);
  height: 100%;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  .loading-img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }
}

.selectedlogo {
  width: 100%;
  padding: 0.5rem 1rem;
  text-align: center;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
  h3 { font-size: 20px; }
  background: $chatbotPanelBg;
  position: relative;
  span {
    font-style: italic;
    color: $colorTextSemi;
  }
}

.action-container {
  font-size: 1rem;
  background: $chatbotPanelBg;
  height: 100%;
  width: 100%;
  .prompt-wrap {
    padding: 1rem;
    padding-top: 0.5rem;
    border-top: 1px solid $colorBorderDash;
    .prompt-wrap {
      padding: 1rem;
      padding-top: 0.5rem;
      border-top: 1px solid $colorBorderDash;
      &__options {
        display: flex;
        gap: 0.5rem;
      }
    }
    .form-group {
      margin-bottom: 0.5rem;
    }
    .custom-select {
      background-color: white;
    }
  }
}

.modal-panel {
  display: flex;
  flex-direction: column;
  justify-content: space-between;
}

.gap-sm {
  gap: 50px;
}

.gap-lg {
  gap: 110px;
}
</style>